import { useEffect, useState } from "react";
import { ICON_BASE_PATH } from "../../../../utils/config";
import { TextBox, type TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { t } from "i18next";

interface BeneficiaryNameProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onValidate?: (field: string, isValid: boolean) => void;
  fieldName?: string;
  validationMessage?: string;
  triggerValidation?: boolean;
  starlable?: boolean;
  disable?: boolean;
  max?: number;
  iconSrc?: string;
}

const BeneficiaryName: React.FC<BeneficiaryNameProps> = ({
  label = "Beneficiary Name",
  value,
  onChange,
  onValidate,
  fieldName = "beneficiaryName",
  validationMessage,
  starlable = true,
  disable = false,
  max = 50,
  triggerValidation = false,
  iconSrc,
}) => {
  const [error, setError] = useState<string>("");

  const sanitize = (s: string) => {
    const cleaned = s.replace(/[^a-zA-Z\s]/g, "").replace(/\s+/g, " ");
    return cleaned.replace(
      /\b([a-zA-Z])([a-zA-Z]*)/g,
      (_, first, rest) => first.toUpperCase() + rest.toLowerCase(),
    );
  };

  const validate = (val: string): string => {
    if (!val.trim()) return `${t("please_enter_the")} ${label}.`;
    if (!/^[A-Za-z\s]+$/.test(val)) return `${t("only_alphabet_space_allow")}.`;
    if (val.trim().length < 2)
      return `${label} ${t("must_contain_at_least_letter")}.`;
    if (val.trim().length > 50)
      return `${label} ${t("cannot_exceed_50_char")}.`;
    return "";
  };

  const updateValidation = (val: string) => {
    const newError = validate(val);
    setError(newError);
    onValidate?.(fieldName, !newError);
  };

  const handleChange = (e: TextBoxChangeEvent) => {
    const raw = String(e.value ?? "");
    let filtered = sanitize(raw);
    if (max && filtered.length > max) filtered = filtered.slice(0, max);
    onChange(filtered);
  };

  const handleBlur = () => {
    updateValidation(value);
  };

  const isInvalid = !!error;

  useEffect(() => {
    if (triggerValidation) {
      updateValidation(value);
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (validationMessage && triggerValidation) {
      setError(validationMessage);
      onValidate?.(fieldName, false);
    }
  }, [validationMessage, triggerValidation]);

  return (
    <div className="gx_input_container">
      <label
        className={`input-label gx-font-semibold ${
          value?.trim() ? "floating" : ""
        }`}
      >
        {t(label)}
        {starlable && (
          <span
            className={`gx_input_star ${
              value?.trim() ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div className={`gx_input_cont ${isInvalid ? "invalid" : "valid"}`}>
        <img
          src={iconSrc ? iconSrc : `${ICON_BASE_PATH}name-input.svg`}
          alt="name"
        />
        <TextBox
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          autoComplete="off"
          max={max}
          disabled={disable}
          className="gx-textbox-css"
        />
      </div>

      {isInvalid && (
        <div className="gx_input_validationmessage gx-font-regular">
          {error}
        </div>
      )}
    </div>
  );
};

export default BeneficiaryName;
